import { authFetch } from '../utils/authFetch';
import { AWS_LAMBDA_ENDPOINT } from '../utils/constants';

class TutorialService {
  /**
   * Get tutorial structure
   * @returns {Promise<Object>} Tutorial structure with hierarchical data
   */
  static async getTutorialStructure() {
    try {
      const url = `${AWS_LAMBDA_ENDPOINT}/getTutorialStructure`;
      
      const response = await authFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      return parsed;
    } catch (error) {
      console.error('Error fetching tutorial structure:', error);
      throw error;
    }
  }

  /**
   * Get user's tutorial progress
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User's tutorial content status
   */
  static async getUserProgress(userId) {
    try {
      const url = `${AWS_LAMBDA_ENDPOINT}/getUserProgress?userId=${encodeURIComponent(userId)}`;
      
      const response = await authFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching user progress:', error);
      throw error;
    }
  }

  /**
   * Update user's progress for a specific content
   * @param {string} userId - User ID
   * @param {string} itemId - Item ID
   * @param {boolean} completed - Completion status
   * @returns {Promise<Object>} Updated progress data
   */
  static async updateUserProgress(userId, itemId, completed = true) {
    try {
      const url = `${AWS_LAMBDA_ENDPOINT}/updateUserProgress`;
      
      const response = await authFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          itemId,
          completed
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating user progress:', error);
      throw error;
    }
  }

  /**
   * Initialize tutorial status for a user using resolveRequiredStatusForUser
   * @param {string} userId - User ID
   * @param {string} settingMode - Setting mode (default, group, customized) - currently ignored, handled by resolve function
   * @param {string} groupId - Group ID (required for group mode) - currently ignored, handled by resolve function
   * @returns {Promise<Object>} Initialized tutorial status
   */
  static async initializeUserTutorialStatus(userId, settingMode = 'default', groupId = null) {
    try {
      const url = `${AWS_LAMBDA_ENDPOINT}/resolveRequiredStatusForUser`;
      
      const requestBody = {
        userId
      };
      
      const response = await authFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Extract the first result from the batch response
      if (data.results && data.results.length > 0) {
        return data.results[0];
      } else {
        throw new Error('No results returned from resolveRequiredStatusForUser');
      }
    } catch (error) {
      console.error('Error initializing user tutorial status:', error);
      throw error;
    }
  }

  /**
   * Get presigned URL for S3 content
   * @param {string} s3Key - S3 object key
   * @param {string} contentType - Content type (video, pdf, etc.)
   * @param {string} operation - Operation type (get, put)
   * @returns {Promise<Object>} Presigned URL data
   */
  static async getPresignedURL(s3Key, contentType = 'video', operation = 'get') {
    try {
      const url = `${AWS_LAMBDA_ENDPOINT}/getPresignedURL?s3Key=${encodeURIComponent(s3Key)}&contentType=${encodeURIComponent(contentType)}&operation=${encodeURIComponent(operation)}`;
      
      const response = await authFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      throw error;
    }
  }

  /**
   * Upload tutorial structure (admin function)
   * @param {Array} tutorialData - Array of tutorial items
   * @returns {Promise<Object>} Upload result
   */
  static async uploadTutorialStructure(tutorialData) {
    try {
      const url = `${AWS_LAMBDA_ENDPOINT}/uploadTutorialStructure`;
      
      const response = await authFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tutorialData
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error uploading tutorial structure:', error);
      throw error;
    }
  }
}

export default TutorialService; 